import { optionalPhoneSchema, type AuthUser } from '@hemmafixa/shared';
import {
  Avatar,
  Badge,
  Button,
  Card,
  FormField,
  Input,
  PageHeader,
  PasswordInput,
  toast,
} from '@hemmafixa/ui';
import { useState, type FormEvent } from 'react';
import { useI18n } from '@/i18n/context';
import { ApiError } from '@/lib/api';
import { useChangePassword, useCurrentUser, useUpdateProfile } from '@/features/auth/api';
import { authErrorMessage, newPasswordErrors } from '@/features/auth/auth-errors';
import { NotificationSettings } from '@/features/notifications/notification-settings';

/**
 * Account settings for every area, on one screen: who you are and your details on the left, notifications and
 * password on the right.
 */
export function SettingsPage() {
  const { t } = useI18n();
  const user = useCurrentUser();

  return (
    <div className="grid gap-4">
      <PageHeader title={t.settings.title} description={t.settings.description} />
      <Card className="flex flex-wrap items-center gap-4 p-5">
        <div className="rounded-full border-[3px] border-primary border-b-secondary/20 border-l-secondary/20 p-1">
          <Avatar seed={user.id} name={user.name} size="lg" />
        </div>
        <div className="grid min-w-0 flex-1 gap-1">
          <p className="truncate text-lg font-bold text-secondary">{user.name}</p>
          <p className="truncate text-sm text-muted-foreground">{user.email}</p>
        </div>
        <div className="flex flex-wrap gap-1.5">
          {user.roles.map((role) => (
            <Badge key={role} tone="brand">
              {t.areas[role]}
            </Badge>
          ))}
        </div>
      </Card>
      <div className="grid items-start gap-4 xl:grid-cols-2">
        <div className="grid gap-4">
          <ProfileForm user={user} />
          <PasswordForm email={user.email} />
        </div>
        <NotificationSettings />
      </div>
    </div>
  );
}

function ProfileForm({ user }: { user: AuthUser }) {
  const { t } = useI18n();
  const s = t.settings;
  const update = useUpdateProfile();
  const [name, setName] = useState(user.name);
  const [phone, setPhone] = useState(user.phone ?? '');
  const [errors, setErrors] = useState<{ name?: string; phone?: string }>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = {
      name: name.trim().length < 2 || name.trim().length > 100 ? s.nameInvalid : undefined,
      phone: optionalPhoneSchema.safeParse(phone).success ? undefined : s.phoneInvalid,
    };
    setErrors(found);
    if (found.name || found.phone) return;
    update.mutate(
      { name, phone },
      {
        onSuccess: (saved) => {
          setPhone(saved.phone ?? '');
          toast.success(s.savedToast);
        },
        onError: (error) => toast.error(authErrorMessage(error, t)),
      },
    );
  };

  return (
    <Card className="p-5 sm:p-6">
      <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" noValidate>
        <h2 className="font-semibold sm:col-span-2">{s.profile}</h2>
        <FormField label={s.name} error={errors.name} required>
          <Input value={name} onChange={(event) => setName(event.target.value)} autoComplete="name" />
        </FormField>
        <FormField label={s.phone} hint={s.phoneHint} error={errors.phone}>
          <Input
            type="tel"
            value={phone}
            onChange={(event) => setPhone(event.target.value)}
            autoComplete="tel"
            placeholder="070-123 45 67"
          />
        </FormField>
        <FormField label={s.email} hint={s.emailHint} className="sm:col-span-2">
          <Input type="email" value={user.email} readOnly autoComplete="email" />
        </FormField>
        <div className="flex justify-end sm:col-span-2">
          <Button type="submit" loading={update.isPending}>
            {t.common.save}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PasswordForm({ email }: { email: string }) {
  const { t } = useI18n();
  const s = t.settings;
  const change = useChangePassword();
  const [current, setCurrent] = useState('');
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<{ current?: string; password?: string; repeat?: string }>({});

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = {
      current: current ? undefined : t.auth.passwordRequired,
      ...newPasswordErrors(password, repeat, t),
    };
    setErrors(found);
    if (found.current || found.password || found.repeat) return;
    change.mutate(
      { currentPassword: current, newPassword: password },
      {
        onSuccess: () => {
          setCurrent('');
          setPassword('');
          setRepeat('');
          toast.success(s.passwordChanged);
        },
        onError: (error) => {
          if (error instanceof ApiError && error.code === 'VALIDATION_FAILED') {
            setErrors({ current: s.wrongCurrentPassword });
          } else {
            toast.error(authErrorMessage(error, t));
          }
        },
      },
    );
  };

  const passwordProps = { showLabel: t.auth.showPassword, hideLabel: t.auth.hidePassword };
  return (
    <Card className="p-5 sm:p-6">
      <form onSubmit={submit} className="grid gap-4" noValidate>
        <div className="grid gap-1">
          <h2 className="font-semibold">{s.password}</h2>
          <p className="text-sm text-muted-foreground">{s.passwordText}</p>
        </div>
        {/* Lets password managers update the saved password for this account */}
        <Input type="email" autoComplete="username" value={email} readOnly hidden />
        <FormField label={s.currentPassword} error={errors.current} required>
          <PasswordInput
            autoComplete="current-password"
            value={current}
            onChange={(event) => setCurrent(event.target.value)}
            {...passwordProps}
          />
        </FormField>
        <div className="grid gap-4 sm:grid-cols-2">
          <FormField
            label={t.auth.newPassword}
            hint={t.auth.newPasswordHint}
            error={errors.password}
            required
          >
            <PasswordInput
              autoComplete="new-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              {...passwordProps}
            />
          </FormField>
          <FormField label={t.auth.repeatPassword} error={errors.repeat} required>
            <PasswordInput
              autoComplete="new-password"
              value={repeat}
              onChange={(event) => setRepeat(event.target.value)}
              {...passwordProps}
            />
          </FormField>
        </div>
        <div className="flex justify-end">
          <Button type="submit" variant="outline" loading={change.isPending}>
            {s.changePassword}
          </Button>
        </div>
      </form>
    </Card>
  );
}
